import { MemberUtils } from "@shared/member.utils";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { BackButton } from "../../components/common/BackButton";
import { FormContainer } from "../../components/common/Form";
import { PageTitle } from "../../components/common/PageData";
import { useCourseStore } from "../../stores/course.store";
import { useSeasonStore } from "../../stores/season.store";
import { useUserStore } from "../../stores/user.store";
import type { CourseType } from "../../types/course";
import { CompanionField } from "./components/CompanionField";
import { CoursePaymentNotice } from "./CoursePaymentNotice";
import { CoursePaymentLayout } from "./CoursePendingRecap";
import { CourseTypeField } from "./components/CourseTypeField";
import { LevelField } from "./components/LevelField";
import { SubscriptionFormActions } from "./components/SubscriptionFormActions";
import { SubscriptionMemberInfo } from "./components/SubscriptionMemberInfo";
import { useAvailableCompanions } from "./hooks/useAvailableCompanions";
import { useSubscriptionMode } from "./hooks/useSubscriptionMode";
import { useCourseSubscriptionContext } from "./useCourseSubscriptionContext";

const SubscriptionStatus = styled.div`
  margin: 16px 0;
`;

const PriceDisplay = styled.div`
  background-color: #e6f3ff;
  padding: 16px;
  border-radius: 8px;
  margin-top: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 18px;
  font-weight: bold;
`;

type FormInputs = {
  type: CourseType;
  level: string;
  companion_id: string;
};

export const SingleCourseSubscription = () => {
  const { memberId } = useParams<{ memberId: string }>();
  const navigate = useNavigate();
  const { profile } = useUserStore();
  const { currentSeason } = useSeasonStore();
  const { currentCourse } = useCourseStore();
  const { tempSubscriptions, setTempSubscriptions } =
    useCourseSubscriptionContext();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormInputs>();

  // Find the member
  const member = profile?.members.find((m) => m.id === memberId);

  // Get subscription mode and data
  const { mode, dbSubscription, tempSubscription, isReadOnly } =
    useSubscriptionMode(member, currentCourse, tempSubscriptions);
  // The saved subscription, paid or still pending payment
  const subscription = dbSubscription ?? tempSubscription;

  // Get available companions
  const availableCompanions = useAvailableCompanions(
    profile?.members,
    memberId,
    currentCourse,
    currentSeason,
    tempSubscriptions
  );

  // Determine subscription status based on dates
  const getSubscriptionStatus = (): "before_opening" | "open" | "closed" => {
    if (!currentCourse) {
      return "closed";
    }
    const now = Date.now();
    const startDate = new Date(currentCourse.subscription_start_date).getTime();
    const endDate = new Date(currentCourse.subscription_end_date).getTime();

    if (now < startDate) {
      return "before_opening";
    }
    if (now > endDate) {
      return "closed";
    }
    return "open";
  };

  const subscriptionStatus = getSubscriptionStatus();

  // If subscriptions are before opening, redirect back (no access yet)
  // If closed, allow viewing existing subscriptions but make readonly
  useEffect(() => {
    if (!currentSeason || !currentCourse) {
      navigate("/account/courses");
      return;
    }

    if (subscriptionStatus === "before_opening") {
      navigate("/account/courses");
    }
    // Allow access when closed if there's an existing subscription (view only)
    if (
      subscriptionStatus === "closed" &&
      !dbSubscription &&
      !tempSubscription
    ) {
      navigate("/account/courses");
    }
  }, [
    currentSeason,
    currentCourse,
    subscriptionStatus,
    navigate,
    dbSubscription,
    tempSubscription,
  ]);

  // Pre-fill form when component mounts or data changes
  useEffect(() => {
    if (subscription) {
      reset({
        type: subscription.type,
        level: subscription.level.toString(),
        companion_id: subscription.companion_id || "",
      });
    }
  }, [subscription, reset]);

  const onSubmit = async (data: FormInputs) => {
    if (!memberId || !currentSeason || !currentCourse) {
      return;
    }

    const subscriptionData = {
      type: data.type,
      level: parseInt(data.level, 10) as 1 | 2 | 3,
      companion_id: data.companion_id || null,
    };

    // Save to temporary subscriptions (create or update)
    setTempSubscriptions((prev) => {
      const next = new Map(prev);
      next.set(memberId, {
        subscriber_id: memberId,
        course_id: currentCourse.id,
        ...subscriptionData,
      });
      return next;
    });

    // Navigate back to courses listing
    navigate("/account/courses");
  };

  const handleCancel = () => {
    navigate("/account/courses");
  };

  const handleDelete = () => {
    if (!memberId) {
      return;
    }

    if (!window.confirm("Sei sicuro di voler eliminare?")) {
      return;
    }

    setTempSubscriptions((prev) => {
      const next = new Map(prev);
      next.delete(memberId);
      return next;
    });

    navigate("/account/courses");
  };

  // Loading states
  if (!profile) {
    return (
      <FormContainer>
        <BackButton />
        <PageTitle>ISCRIZIONE CORSO</PageTitle>
        <p>Caricamento...</p>
      </FormContainer>
    );
  }

  if (!currentSeason || !currentCourse) {
    return (
      <FormContainer>
        <BackButton onClick={() => navigate("/account/courses")} />
        <PageTitle>ISCRIZIONE CORSO</PageTitle>
        <p>Le iscrizioni ai corsi sono temporaneamente chiuse.</p>
      </FormContainer>
    );
  }

  if (!member) {
    return (
      <FormContainer>
        <BackButton />
        <PageTitle>ISCRIZIONE CORSO</PageTitle>
        <p>Membro non trovato.</p>
      </FormContainer>
    );
  }

  const companionId = subscription?.companion_id;
  const companionMember = companionId
    ? profile.members.find((m) => m.id === companionId)
    : null;

  // Get member type to determine if we show companion field
  const memberType = MemberUtils.getMemberType(
    member.date_of_birth,
    currentSeason
  );
  const showCompanionField = memberType !== "adult"; // Hide companion field for adults

  // Make all fields readonly if subscriptions are closed
  const isFormReadOnly = subscriptionStatus === "closed" || isReadOnly;

  return (
    <FormContainer>
      <BackButton onClick={() => navigate("/account/courses")} />
      <PageTitle>ISCRIZIONE CORSO</PageTitle>

      {dbSubscription?.status !== "confirmed" && (
        <CoursePaymentNotice stage={dbSubscription ? "pending" : "before"} />
      )}

      <SubscriptionMemberInfo
        member={member}
        currentSeason={currentSeason}
        currentCourse={currentCourse}
      />
      {dbSubscription && (
        <SubscriptionStatus>
          <CoursePaymentLayout member={member} subscription={dbSubscription}>
            <span>
              Stato: <strong>{dbSubscription.status === "pending" ? "In attesa di verifica" : "Confermato"}</strong>
            </span>
          </CoursePaymentLayout>
        </SubscriptionStatus>
      )}

      <form onSubmit={handleSubmit(onSubmit)}>
        <CourseTypeField
          isReadOnly={isFormReadOnly}
          currentType={subscription?.type}
          register={register}
          errors={errors}
        />

        <LevelField
          isReadOnly={isFormReadOnly}
          currentLevel={subscription?.level}
          register={register}
          errors={errors}
        />

        {showCompanionField && (
          <CompanionField
            isRequired={false}
            isReadOnly={isFormReadOnly}
            currentCompanion={companionMember}
            availableCompanions={availableCompanions}
            register={register}
            errors={errors}
          />
        )}

        {mode !== "view-paid" && (
          <PriceDisplay>
            <span>PREZZO</span>
            <span>EUR {currentCourse.price.toFixed(2)}</span>
          </PriceDisplay>
        )}

        <SubscriptionFormActions
          canCreate={subscriptionStatus === "open"}
          mode={mode}
          isSubmitting={isSubmitting}
          onCancel={handleCancel}
          onDelete={mode === "edit-temporary" ? handleDelete : undefined}
        />
      </form>
    </FormContainer>
  );
};
